import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { deleteRoleApi, listOfRoleApi, listOfRolePermissionApi } from "../../Api/roleApi";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess, MenuConstants } from "../../Constant/common";
import { listOfOrganizationApi } from "../../Api/OrganizationApi";
import { Active, Deactive, Search, SuperAdmin } from "../../Constant";
import {
  activateLabel,
  areYouSureLabel,
  confirmationLabel,
  deactivateLabel,
  editRoleLabel,
  itemLabel,
  newRoleLabel,
  roleLabel,
  viewRoleLabel,
} from "../../Constant/Role";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import { SelectPlaceHolder } from "../../Constant/validation";
import { staffEnums } from "../../Constant/Staff/staff";
import { formFields } from "../../Constant/HR_Staff/hrEnums";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { useSelectedOrganization } from "../OrganizationContext";
import {
  Edit2,
  Eye,
  ToggleLeft,
  ToggleRight,
  Plus,
  Shield,
  ChevronLeft,
  ChevronRight,
  Search as SearchIcon,
  X,
  AlertCircle,
} from "react-feather";
import BaseTooltip from "../../BaseComponents/BaseTooltip";

const RoleCard = ({ role, onEdit, onView, onToggle, masterPermissions = [] }) => {
  const isActive = role?.isActive;

  const currentUserRole = localStorage.getItem("role");
  const currentUserPermissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions")) || [];
    } catch {
      return [];
    }
  }, []);

  const hasSalesPermission = useMemo(() => {
    if (currentUserRole === SuperAdmin) return true;
    return currentUserPermissions.some(
      (p) =>
        p.parent_module_name?.trim()?.toLowerCase() === "sales" ||
        p.module_name?.trim()?.toLowerCase() === "sales",
    );
  }, [currentUserRole, currentUserPermissions]);

  const hasSupportPermission = useMemo(() => {
    if (currentUserRole === SuperAdmin) return true;
    return currentUserPermissions.some(
      (p) =>
        p.parent_module_name?.trim()?.toLowerCase() === "support" ||
        p.module_name?.trim()?.toLowerCase() === "support",
    );
  }, [currentUserRole, currentUserPermissions]);

  const filterModule = useCallback(
    (item) => {
      const parent = item?.parent_module_name || item?.masterModule?.parent_module_name;
      const mod = item?.module_name || item?.masterModule?.module_name;

      if (!parent) return false;

      const validParentNames = Object.values(MenuConstants);
      if (!validParentNames.includes(parent)) return false;

      if (parent === "Master" && mod?.trim()?.toLowerCase() === "organization") {
        return false;
      }

      if (parent === "Sales" && !hasSalesPermission) {
        return false;
      }

      if (parent === "Support" && !hasSupportPermission) {
        return false;
      }

      return true;
    },
    [hasSalesPermission, hasSupportPermission],
  );

  const maxPerms = useMemo(() => {
    const filtered = masterPermissions.filter(filterModule);
    return filtered.length || 35;
  }, [masterPermissions, filterModule]);

  const visiblePermissions = useMemo(() => {
    const list = (role?.rolePermission || []).filter(filterModule);
    return list;
  }, [role?.rolePermission, filterModule]);

  const totalPerms = visiblePermissions.length;

  /* Count by category — just names + counts, no individual chips */
  const categories = useMemo(() => {
    const map = {};
    visiblePermissions.forEach((p) => {
      const parent = p?.masterModule?.parent_module_name || "Other";
      if (!map[parent]) map[parent] = 0;
      map[parent]++;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [visiblePermissions]);

  const pct = maxPerms > 0 ? Math.min(Math.round((totalPerms / maxPerms) * 100), 100) : 0;
  const pctColor = pct > 60 ? "#10b981" : pct > 30 ? "#6366f1" : "#94a3b8";

  const isDefault = !!role?.is_default;

  return (
    <div
      className="animate-slide-up"
      style={{
        background: "var(--glass-bg)",
        border: "1px solid var(--glass-border)",
        borderRadius: 18,
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        transition: "box-shadow 0.2s, transform 0.2s",
        opacity: isActive ? 1 : 0.55,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.1)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* ── Top row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            flexShrink: 0,
            background: isActive ? "rgba(99,102,241,0.08)" : "var(--icon-btn-bg)",
            border: "1px solid var(--glass-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Shield
            size={17}
            strokeWidth={1.75}
            style={{ color: isActive ? "var(--brand-indigo)" : "var(--text-muted)" }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {role?.role_name}
            {isDefault && (
              <span
                style={{
                  fontSize: "0.625rem",
                  background: "rgba(99,102,241,0.12)",
                  color: "var(--brand-indigo)",
                  padding: "2px 6px",
                  borderRadius: 6,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Default
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: isActive ? "#10b981" : "#94a3b8",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: "0.6875rem",
                color: isActive ? "#10b981" : "#94a3b8",
                fontWeight: 600,
              }}
            >
              {isActive ? Active : Deactive}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          {[
            {
              label: viewRoleLabel,
              Icon: Eye,
              color: "#6366f1",
              bg: "rgba(99,102,241,0.1)",
              border: "rgba(99,102,241,0.2)",
              onClick: () => onView(role.id),
              disabled: false,
            },
            {
              label: editRoleLabel,
              Icon: Edit2,
              color: "#f59e0b",
              bg: "rgba(245,158,11,0.1)",
              border: "rgba(245,158,11,0.2)",
              onClick: () => onEdit(role.id),
              disabled: !isActive,
            },
            ...(!isDefault
              ? [
                  {
                    label: isActive ? deactivateLabel : activateLabel,
                    Icon: isActive ? ToggleRight : ToggleLeft,
                    color: isActive ? "#f43f5e" : "#10b981",
                    bg: isActive ? "rgba(244,63,94,0.1)" : "rgba(16,185,129,0.1)",
                    border: isActive ? "rgba(244,63,94,0.2)" : "rgba(16,185,129,0.2)",
                    onClick: () => onToggle(role.id, role.isActive),
                    disabled: false,
                  },
                ]
              : []),
          ].map(({ label, Icon, color, bg, border, onClick, disabled }) => (
            <BaseTooltip key={label} label={label} color={color}>
              <button
                onClick={onClick}
                disabled={disabled}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  border: `1px solid ${border}`,
                  background: bg,
                  color: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.35 : 1,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!disabled) {
                    e.currentTarget.style.opacity = "0.75";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = disabled ? "0.35" : "1";
                }}
              >
                <Icon size={12} strokeWidth={2} />
              </button>
            </BaseTooltip>
          ))}
        </div>
      </div>

      {/* ── Access bar ── */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span
            style={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Access Level
          </span>
          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: pctColor }}>
            {totalPerms} permissions
          </span>
        </div>
        <div
          style={{
            height: 4,
            borderRadius: 999,
            background: "var(--icon-btn-bg)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 999,
              width: `${pct}%`,
              background: pctColor,
              transition: "width 0.6s ease",
            }}
          />
        </div>
      </div>

      {/* ── Category summary ── */}
      {categories.length > 0 ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                background: "var(--glass-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Shield size={9} strokeWidth={2.5} style={{ color: "var(--text-muted)" }} />
            </div>
            <span
              style={{
                fontSize: "0.625rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.09em",
                color: "var(--text-muted)",
              }}
            >
              Can access
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {categories.map(([cat, count]) => (
              <span
                key={cat}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 10px",
                  borderRadius: 8,
                  background: "var(--icon-btn-bg)",
                  border: "1px solid var(--glass-border)",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "var(--text-secondary)",
                }}
              >
                {cat}
                <span
                  style={{
                    fontSize: "0.625rem",
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    background: "var(--glass-border)",
                    borderRadius: 4,
                    padding: "1px 5px",
                  }}
                >
                  {count}
                </span>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: "0.75rem",
            color: "var(--text-muted)",
            textAlign: "center",
          }}
        >
          No permissions assigned
        </p>
      )}
    </div>
  );
};

/* ── Main page ── */
const Role = () => {
  document.title = roleLabel;
  const navigate = useNavigate();
  const { setLoading } = usePageLoader();
  const { selectedOrganization: globalOrgId } = useSelectedOrganization();
  const isSuperAdmin = localStorage.getItem("role") === SuperAdmin;
  const [btnLoader, setBtnLoader] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize] = useState(12);
  const [searchValue, setSearchValue] = useState("");
  const [columnName] = useState("id");
  const [sortOrder] = useState("DESC");
  const [activeId, setActiveId] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [roleData, setRoleData] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    return isSuperAdmin
      ? null
      : localStorage.getItem("organizationId")
        ? Number(localStorage.getItem("organizationId"))
        : null;
  });
  const [confirmModal, setConfirmModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [masterPermissions, setMasterPermissions] = useState([]);

  /* ── Fetch ── */
  const fetchRoles = useCallback(() => {
    const orgId = isSuperAdmin
      ? selectedOrganization
      : localStorage.getItem("organizationId")
        ? Number(localStorage.getItem("organizationId"))
        : null;
    if (orgId === null) return;
    setLoading(true);
    const payload = {
      order: [columnName, sortOrder],
      search: searchValue,
      pageNumber: currentPage,
      pageSize: customPageSize,
    };
    const finalPayload = orgId ? { ...payload, condition: { organizationId: orgId } } : payload;

    listOfRoleApi(finalPayload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setRoleData(resp?.data?.listOfRoles || []);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setCurrentPage(resp?.data?.currentPage);
        } else setRoleData([]);
      })
      .catch(() => setRoleData([]))
      .finally(() => setLoading(false));
  }, [
    columnName,
    sortOrder,
    currentPage,
    customPageSize,
    searchValue,
    selectedOrganization,
    isSuperAdmin,
    setLoading,
  ]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const orgId = isSuperAdmin
      ? selectedOrganization
      : localStorage.getItem("organizationId")
        ? Number(localStorage.getItem("organizationId"))
        : null;
    if (!orgId) {
      setMasterPermissions([]);
      return;
    }
    listOfRolePermissionApi({ organization_id: Number(orgId) })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setMasterPermissions(resp?.data?.listOfRolePermission || []);
        } else {
          setMasterPermissions([]);
        }
      })
      .catch(() => setMasterPermissions([]));
  }, [selectedOrganization, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) {
      listOfOrganizationApi()
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const list = resp?.data?.listOfOrganizations || [];
            setOrganizationList(
              list.map((item) => ({
                value: item.id,
                label: item.organization_name,
              })),
            );
            if (globalOrgId) {
              setSelectedOrganization(Number(globalOrgId));
            } else if (list.length > 0) {
              setSelectedOrganization(Number(list[0].id));
            }
          }
        })
        .catch(() => {});
    } else {
      const orgId = localStorage.getItem("organizationId");
      if (orgId) setSelectedOrganization(Number(orgId));
    }
  }, [isSuperAdmin, globalOrgId]);

  /* ── Search debounce ── */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(searchInput);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ── Toggle active ── */
  const handleToggle = (id, status) => {
    setActiveId(id);
    setActiveStatus(status);
    setConfirmModal(true);
  };

  const confirmToggle = () => {
    setBtnLoader(true);
    deleteRoleApi(activeId, { isActive: !activeStatus })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message);
          fetchRoles();
          setConfirmModal(false);
        } else toast.error(resp?.message);
      })
      .catch((err) => toast.error(err?.data?.response?.message || err?.message))
      .finally(() => setBtnLoader(false));
  };

  const activeRole = roleData.find((r) => r.id === activeId);

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {roleLabel} Management
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Define roles and control what each role can access
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Summary pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 14px",
              borderRadius: 10,
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.15)",
              fontSize: "0.8125rem",
            }}
          >
            <Shield size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
            <span style={{ fontWeight: 700, color: "var(--brand-indigo)" }}>{totalRecords}</span>
            <span style={{ color: "var(--text-muted)" }}>roles</span>
          </div>
          <BaseButton
            color="primary"
            onClick={() => navigate("/addRole")}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={15} strokeWidth={2.5} /> {newRoleLabel}
          </BaseButton>
        </div>
      </div>

      {/* ── How roles work banner ── */}
      <div
        style={{
          padding: "14px 18px",
          borderRadius: 16,
          background: "linear-gradient(135deg,rgba(99,102,241,0.06),rgba(139,92,246,0.04))",
          border: "1px solid rgba(99,102,241,0.12)",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            flexShrink: 0,
            background: "rgba(99,102,241,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Shield size={16} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
        </div>
        <div>
          <p
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            How roles work
          </p>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: "0.8125rem",
              color: "var(--text-muted)",
              lineHeight: 1.6,
            }}
          >
            Each role defines what a staff member can do in the system. Assign a role to a staff
            member to grant them access to specific modules. You can activate or deactivate roles at
            any time — deactivated roles cannot be assigned to new staff.
          </p>
        </div>
      </div>

      {/* ── Filters row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {/* Search */}
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <SearchIcon
            size={14}
            strokeWidth={2}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder={`${Search} roles…`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            ref={(el) => {
              if (el) el.style.setProperty("padding-left", "34px", "important");
            }}
            style={{
              width: "100%",
              padding: "9px 36px 9px 12px",
              borderRadius: 10,
              border: "1px solid var(--glass-border)",
              background: "var(--glass-bg)",
              color: "var(--text-primary)",
              fontSize: "0.875rem",
              outline: "none",
            }}
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                padding: 2,
                cursor: "pointer",
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                lineHeight: 1,
              }}
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Org filter (SuperAdmin only) */}
        {isSuperAdmin && (
          <div style={{ minWidth: 220 }}>
            <BaseSelect
              id={formFields.organization}
              name={formFields.organization}
              options={organizationList}
              placeholder={SelectPlaceHolder(staffEnums.ORGANIZATION)}
              value={selectedOrganization}
              mb="mb-0"
              handleChange={(field, selected) => {
                setSelectedOrganization(selected ? Number(selected) : null);
                setCurrentPage(1);
              }}
              handleBlur={() => {}}
              isDisabled={!!globalOrgId}
            />
          </div>
        )}
      </div>

      {/* ── Split Layout for Default vs Custom Roles ── */}
      {roleData.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "3fr 7fr",
            gap: 24,
            alignItems: "start",
          }}
        >
          {/* Left Panel: Default Roles */}
          {(() => {
            const defaultRoles = roleData.filter((r) => !!r.is_default);
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  background: "rgba(99,102,241,0.02)",
                  border: "1px dashed var(--brand-indigo)",
                  borderRadius: 20,
                  padding: "20px",
                }}
              >
                <div style={{ marginBottom: 4 }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "0.9375rem",
                      fontWeight: 800,
                      color: "var(--brand-indigo)",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Shield size={16} strokeWidth={2.5} /> System Default Roles
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      lineHeight: 1.4,
                    }}
                  >
                    These core roles are required by the SaaS platform and cannot be deactivated or
                    deleted.
                  </p>
                </div>
                {(() => {
                  return defaultRoles.length > 0 ? (
                    defaultRoles.map((role) => (
                      <RoleCard
                        key={role.id}
                        role={role}
                        masterPermissions={masterPermissions}
                        onEdit={(id) =>
                          navigate(`/editRole/${id}`, { state: { isDefault: role.is_default } })
                        }
                        onView={(id) =>
                          navigate(`/viewRole/${id}`, { state: { isDefault: role.is_default } })
                        }
                        onToggle={handleToggle}
                      />
                    ))
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.8125rem",
                        color: "var(--text-muted)",
                        textAlign: "center",
                        padding: "10px 0",
                      }}
                    >
                      No system default roles matched in this view.
                    </p>
                  );
                })()}
              </div>
            );
          })()}

          {/* Right Panel: Custom Roles */}
          {(() => {
            const customRoles = roleData.filter((r) => !r.is_default);
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 2 }}>
                <div style={{ marginBottom: 4 }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "0.9375rem",
                      fontWeight: 800,
                      color: "var(--text-primary)",
                    }}
                  >
                    Custom Roles
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      lineHeight: 1.4,
                    }}
                  >
                    Roles created specifically for this organization tenant. You can manage,
                    activate, or deactivate these dynamically.
                  </p>
                </div>
                {customRoles.length > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                      gap: 16,
                    }}
                  >
                    {(() => {
                      return customRoles.map((role) => (
                        <RoleCard
                          key={role.id}
                          role={role}
                          masterPermissions={masterPermissions}
                          onEdit={(id) =>
                            navigate(`/editRole/${id}`, { state: { isDefault: role.is_default } })
                          }
                          onView={(id) =>
                            navigate(`/viewRole/${id}`, { state: { isDefault: role.is_default } })
                          }
                          onToggle={handleToggle}
                        />
                      ));
                    })()}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "40px 20px",
                      gap: 12,
                      background: "var(--glass-bg)",
                      border: "1px dashed var(--glass-border)",
                      borderRadius: 16,
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "var(--text-secondary)",
                      }}
                    >
                      No Custom Roles Defined
                    </p>
                    <BaseButton
                      color="primary"
                      onClick={() => navigate("/addRole")}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.8125rem",
                        padding: "6px 12px",
                      }}
                    >
                      <Plus size={14} strokeWidth={2.5} /> Add Custom Role
                    </BaseButton>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 20px",
            gap: 16,
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
          }}
        >
          <div
            className="animate-float-y"
            style={{
              width: 72,
              height: 72,
              borderRadius: 20,
              background: "rgba(99,102,241,0.1)",
              border: "1px solid rgba(99,102,241,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AlertCircle size={32} strokeWidth={1.5} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{ margin: 0, fontWeight: 700, fontSize: "1rem", color: "var(--text-primary)" }}
            >
              No roles found
            </p>
            <p style={{ margin: "4px 0 0", fontSize: "0.875rem", color: "var(--text-muted)" }}>
              {searchValue
                ? "Try a different search term"
                : "Create your first role to get started"}
            </p>
          </div>
          {!searchValue && (
            <BaseButton
              color="primary"
              onClick={() => navigate("/addRole")}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Plus size={15} strokeWidth={2.5} /> {newRoleLabel}
            </BaseButton>
          )}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              border: "1px solid var(--glass-border)",
              background: "var(--icon-btn-bg)",
              color: "var(--text-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={15} strokeWidth={2.5} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setCurrentPage(p)}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                border:
                  p === currentPage
                    ? "1.5px solid var(--brand-indigo)"
                    : "1px solid var(--glass-border)",
                background: p === currentPage ? "rgba(99,102,241,0.12)" : "var(--icon-btn-bg)",
                color: p === currentPage ? "var(--brand-indigo)" : "var(--text-secondary)",
                fontWeight: p === currentPage ? 700 : 500,
                fontSize: "0.875rem",
                cursor: "pointer",
              }}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              border: "1px solid var(--glass-border)",
              background: "var(--icon-btn-bg)",
              color: "var(--text-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages ? 0.4 : 1,
            }}
          >
            <ChevronRight size={15} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* ── Confirm toggle modal ── */}
      <BaseModal
        isOpen={confirmModal}
        toggler={() => setConfirmModal(false)}
        title={confirmationLabel}
        submitText={activeStatus ? deactivateLabel : activateLabel}
        disabled={btnLoader}
        loader={btnLoader}
        submit={confirmToggle}
      >
        <div style={{ padding: "8px 0" }}>
          <p style={{ margin: 0, fontSize: "0.9375rem", color: "var(--text-primary)" }}>
            {areYouSureLabel}
            <strong style={{ color: activeStatus ? "#f43f5e" : "#10b981" }}>
              {activeStatus ? deactivateLabel : activateLabel}
            </strong>
            {itemLabel}
          </p>
          {activeRole && (
            <p style={{ margin: "8px 0 0", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
              Role: <strong style={{ color: "var(--text-primary)" }}>{activeRole.role_name}</strong>
            </p>
          )}
        </div>
      </BaseModal>
    </div>
  );
};

export default Role;
